//frontend/dispatcher-client/src/components/personnel/PersonnelHolidayWorkFormModal.jsx
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  LuCalendarDays,
  LuClock3,
  LuSave,
  LuTrash2,
  LuTruck,
  LuUser,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  createPersonnelPublicHoliday,
  createPersonnelHolidayWork,
  createPersonnelHolidayWorkBulk,
  updatePersonnelHolidayWork,
  deletePersonnelHolidayWork,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

/* =========================================================
 * CONSTANTS
 * ======================================================= */

const COMPENSATION_TYPES = [
  "EXTRA_PAY",
  "TIME_OFF",
  "EXTRA_PAY_AND_TIME_OFF",
  "NONE",
  "CUSTOM",
];

/* =========================================================
 * HELPERS
 * ======================================================= */

const pad2 =
  (
    value
  ) =>
    String(
      value
    ).padStart(
      2,
      "0"
    );

const toTimeInput =
  (
    value,
    fallback
  ) => {
    if (!value) {
      return fallback;
    }

    const date =
      new Date(
        value
      );

    return [
      pad2(
        date.getHours()
      ),
      pad2(
        date.getMinutes()
      ),
    ].join(":");
  };

const buildLocalDate =
  (
    dateValue,
    timeValue
  ) => {
    if (
      !dateValue ||
      !timeValue
    ) {
      return null;
    }

    const [
      year,
      month,
      day,
    ] =
      String(
        dateValue
      )
        .split("-")
        .map(Number);

    const [
      hours,
      minutes,
    ] =
      String(
        timeValue
      )
        .split(":")
        .map(Number);

    return new Date(
      year,
      month - 1,
      day,
      hours || 0,
      minutes || 0,
      0,
      0
    );
  };

const formatMinutes =
  (
    value,
    t
  ) => {
    const minutes =
      Math.max(
        0,
        Number(
          value
        ) ||
          0
      );

    const hours =
      Math.floor(
        minutes /
          60
      );

    const remainder =
      minutes %
      60;

    const hourUnit =
      t(
        "units.hour_short"
      );

    const minuteUnit =
      t(
        "units.minute_short"
      );

    if (
      remainder ===
      0
    ) {
      return `${hours}${hourUnit}`;
    }

    if (
      hours ===
      0
    ) {
      return `${remainder}${minuteUnit}`;
    }

    return `${hours}${hourUnit} ${remainder}${minuteUnit}`;
  };

/* =========================================================
 * COMPONENT
 * ======================================================= */

export default function PersonnelHolidayWorkFormModal({
  year,
  people = [],
  publicHolidays = [],
  work = null,
  canDelete = false,
  canCreateHoliday = false,
  onClose,
  onChanged,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "pt"
    );

  const isEdit =
    !!work?.id;

  const [
    availableHolidays,
    setAvailableHolidays,
  ] =
    useState(
      publicHolidays
    );

  const [
    newHolidayName,
    setNewHolidayName,
  ] =
    useState(
      ""
    );

  const [
    newHolidayDate,
    setNewHolidayDate,
  ] =
    useState(
      ""
    );

  const [
    newHolidayCountryCode,
    setNewHolidayCountryCode,
  ] =
    useState(
      ""
    );

  const [
    newHolidayRecurring,
    setNewHolidayRecurring,
  ] =
    useState(
      false
    );

  const [
    newHolidayPaid,
    setNewHolidayPaid,
  ] =
    useState(
      false
    );

  const [
    selectedPersonKeys,
    setSelectedPersonKeys,
  ] =
    useState(
      () =>
        work?.personId
          ? [
              `${work.personType}:${work.personId}`,
            ]
          : []
    );

  const [
    publicHolidayId,
    setPublicHolidayId,
  ] =
    useState(
      work?.publicHolidayId
        ? String(
            work.publicHolidayId
          )
        : ""
    );

  const [
    startTime,
    setStartTime,
  ] =
    useState(
      toTimeInput(
        work?.startAt,
        ""
      )
    );

  const [
    endTime,
    setEndTime,
  ] =
    useState(
      toTimeInput(
        work?.endAt,
        ""
      )
    );

  const [
    breakMinutes,
    setBreakMinutes,
  ] =
    useState(
      work?.breakMinutes !=
        null
        ? String(
            work.breakMinutes
          )
        : ""
    );

  const [
    compensationType,
    setCompensationType,
  ] =
    useState(
      work?.compensationType ||
        ""
    );

  const [
    payMultiplier,
    setPayMultiplier,
  ] =
    useState(
      work?.payMultiplier !=
        null
        ? String(
            work.payMultiplier
          )
        : ""
    );

  const [
    compensationMinutes,
    setCompensationMinutes,
  ] =
    useState(
      work?.compensationMinutes !=
        null
        ? String(
            work.compensationMinutes
          )
        : ""
    );

  const [
    amountOverride,
    setAmountOverride,
  ] =
    useState(
      work?.amountOverride !=
        null
        ? String(
            work.amountOverride
          )
        : ""
    );

  const [
    currency,
    setCurrency,
  ] =
    useState(
      work?.currency ||
        ""
    );

  const [
    notes,
    setNotes,
  ] =
    useState(
      work?.notes ||
        ""
    );

  const [
    saving,
    setSaving,
  ] =
    useState(
      false
    );

  const [
    deleting,
    setDeleting,
  ] =
    useState(
      false
    );

  const [
    confirmDelete,
    setConfirmDelete,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const busy =
    saving ||
    deleting;

  useEffect(
    () => {
      setAvailableHolidays(
        publicHolidays
      );
    },
    [
      publicHolidays,
    ]
  );

  /* =======================================================
   * RESOLVED DATA
   * ===================================================== */

  const personOptions =
    useMemo(
      () => {
        const users =
          [];

        const drivers =
          [];

        people.forEach(
          (
            person
          ) => {
            const option = {
              value:
                `${person.personType}:${person.personId}`,

              label:
                person.displayName,

              personType:
                person.personType,

              personId:
                person.personId,

              person,
            };

            if (
              person.personType ===
              "DRIVER"
            ) {
              drivers.push(
                option
              );
            } else {
              users.push(
                option
              );
            }
          }
        );

        const sortOptions =
          (
            first,
            second
          ) =>
            String(
              first.label ||
                ""
            ).localeCompare(
              String(
                second.label ||
                  ""
              ),
              locale
            );

        users.sort(
          sortOptions
        );

        drivers.sort(
          sortOptions
        );

        return [
          {
            label:
              t(
                "requests.filters.users",
                {
                  defaultValue:
                    "Utilizadores",
                }
              ),

            options:
              users,
          },

          {
            label:
              t(
                "requests.filters.drivers",
                {
                  defaultValue:
                    "Motoristas",
                }
              ),

            options:
              drivers,
          },
        ].filter(
          (
            group
          ) =>
            group.options
              .length >
            0
        );
      },
      [
        people,
        locale,
        t,
      ]
    );

  const flatPersonOptions =
    useMemo(
      () =>
        personOptions.flatMap(
          (
            group
          ) =>
            group.options
        ),
      [
        personOptions,
      ]
    );

  const selectedPersonOptions =
    useMemo(
      () =>
        flatPersonOptions.filter(
          (
            option
          ) =>
            selectedPersonKeys.includes(
              option.value
            )
        ),
      [
        flatPersonOptions,
        selectedPersonKeys,
      ]
    );

  const selectedHoliday =
    useMemo(
      () =>
        availableHolidays.find(
          (
            holiday
          ) =>
            Number(
              holiday.id
            ) ===
            Number(
              publicHolidayId
            )
        ) ||
        null,
      [
        availableHolidays,
        publicHolidayId,
      ]
    );

  const isNewHoliday =
    Boolean(
      newHolidayName &&
      !publicHolidayId
    );

  const activeHolidayDate =
    selectedHoliday?.date ||
    newHolidayDate ||
    "";

  const formatHolidayDate =
    (
      value
    ) => {
      if (!value) {
        return "";
      }

      const [
        dateYear,
        month,
        day,
      ] =
        String(
          value
        )
          .split("-")
          .map(Number);

      const date =
        new Date(
          dateYear,
          month - 1,
          day
        );

      return new Intl.DateTimeFormat(
        locale,
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      ).format(
        date
      );
    };

  const holidayOptions =
    useMemo(
      () =>
        availableHolidays
          .slice()
          .sort(
            (
              first,
              second
            ) =>
              String(
                first.date
              ).localeCompare(
                String(
                  second.date
                )
              )
          )
          .map(
            (
              holiday
            ) => ({
              value:
                String(
                  holiday.id
                ),

              label:
                holiday.name,

              holiday,
            })
          ),
      [
        availableHolidays,
      ]
    );

  const selectedHolidayOption =
    useMemo(
      () => {
        if (
          isNewHoliday
        ) {
          return {
            value:
              "__new__",

            label:
              newHolidayName,

            holiday: {
              name:
                newHolidayName,

              date:
                newHolidayDate,

              isQuickNew:
                true,
            },
          };
        }

        return (
          holidayOptions.find(
            (
              option
            ) =>
              option.value ===
              String(
                publicHolidayId
              )
          ) ||
          null
        );
      },
      [
        holidayOptions,
        publicHolidayId,
        isNewHoliday,
        newHolidayName,
        newHolidayDate,
      ]
    );

  const handleCreateHolidayOption =
    (
      inputValue
    ) => {
      if (
        !canCreateHoliday
      ) {
        return;
      }

      const cleanName =
        String(
          inputValue ||
            ""
        ).trim();

      if (!cleanName) {
        return;
      }

      setPublicHolidayId(
        ""
      );

      setNewHolidayName(
        cleanName
      );

      setNewHolidayDate(
        ""
      );
    };

  const compensationTypeLabel =
    (
      type
    ) => {
      const defaults = {
        EXTRA_PAY:
          "Pagamento extra",

        TIME_OFF:
          "Tempo de descanso",

        EXTRA_PAY_AND_TIME_OFF:
          "Pagamento + descanso",

        NONE:
          "Sem compensação",

        CUSTOM:
          "Personalizada",
      };

      return t(
        `compensation_types.${type}`,
        {
          defaultValue:
            defaults[type] ||
            type,
        }
      );
    };

  /* =======================================================
   * COMPENSATION RULE DISPLAY
   * ===================================================== */

  const usesExtraPay =
    [
      "EXTRA_PAY",
      "EXTRA_PAY_AND_TIME_OFF",
      "CUSTOM",
    ].includes(
      compensationType
    );

  const usesTimeOff =
    [
      "TIME_OFF",
      "EXTRA_PAY_AND_TIME_OFF",
      "CUSTOM",
    ].includes(
      compensationType
    );

  /* =======================================================
   * ESTIMATED WORK
   *
   * DISPLAY ONLY.
   * Backend remains authoritative for workedMinutes.
   * ===================================================== */

  const estimatedWorkedMinutes =
    useMemo(
      () => {
        if (
          !activeHolidayDate ||
          !startTime ||
          !endTime
        ) {
          return 0;
        }

        const start =
          buildLocalDate(
            activeHolidayDate,
            startTime
          );

        const end =
          buildLocalDate(
            activeHolidayDate,
            endTime
          );

        if (
          !start ||
          !end ||
          end <= start
        ) {
          return 0;
        }

        const grossMinutes =
          Math.round(
            (
              end -
              start
            ) /
              60000
          );

        return Math.max(
          0,
          grossMinutes -
            (
              Number(
                breakMinutes
              ) ||
              0
            )
        );
      },
      [
        activeHolidayDate,
        startTime,
        endTime,
        breakMinutes,
      ]
    );

      const parsedPayMultiplier =
    useMemo(
      () => {
        const value =
          Number(
            payMultiplier
          );

        return Number.isFinite(
          value
        ) &&
        value > 0
          ? value
          : null;
      },
      [
        payMultiplier,
      ]
    );

  const estimatedPayEquivalentMinutes =
    useMemo(
      () => {
        if (
          !parsedPayMultiplier ||
          !estimatedWorkedMinutes ||
          amountOverride !==
            ""
        ) {
          return 0;
        }

        return Math.round(
          estimatedWorkedMinutes *
            parsedPayMultiplier
        );
      },
      [
        parsedPayMultiplier,
        estimatedWorkedMinutes,
        amountOverride,
      ]
    );

  /* =======================================================
   * PAYLOAD
   * ===================================================== */

  const buildPayload =
    (
      resolvedHolidayId,
      resolvedHolidayDate
    ) => {
      if (
        selectedPersonOptions.length ===
        0
      ) {
        throw new Error(
          t(
            "compensation.form.errors.people_required",
            {
              defaultValue:
                "Selecione pelo menos uma pessoa.",
            }
          )
        );
      }

      if (
        isEdit &&
        selectedPersonOptions.length !==
          1
      ) {
        throw new Error(
          t(
            "compensation.form.errors.single_person_edit",
            {
              defaultValue:
                "O registo editado deve estar associado a uma única pessoa.",
            }
          )
        );
      }

      if (
        !resolvedHolidayId ||
        !resolvedHolidayDate
      ) {
        throw new Error(
          t(
            "compensation.form.errors.holiday_required",
            {
              defaultValue:
                "Selecione ou crie um feriado.",
            }
          )
        );
      }

      const start =
        buildLocalDate(
          resolvedHolidayDate,
          startTime
        );

      const end =
        buildLocalDate(
          resolvedHolidayDate,
          endTime
        );

      if (
        !start ||
        !end ||
        end <= start
      ) {
        throw new Error(
          t(
            "compensation.form.errors.period_invalid",
            {
              defaultValue:
                "A hora final tem de ser posterior à hora inicial.",
            }
          )
        );
      }

      const parsedBreak =
        Number(
          breakMinutes
        );

      if (
        !Number.isFinite(
          parsedBreak
        ) ||
        parsedBreak < 0
      ) {
        throw new Error(
          t(
            "compensation.form.errors.break_invalid",
            {
              defaultValue:
                "Indique uma pausa válida.",
            }
          )
        );
      }

      const totalMinutes =
        Math.round(
          (
            end -
              start
          ) /
            60000
        );

      if (
        parsedBreak >=
        totalMinutes
      ) {
        throw new Error(
          t(
            "compensation.form.errors.break_too_long",
            {
              defaultValue:
                "A pausa tem de ser inferior ao período de trabalho.",
            }
          )
        );
      }

      if (
        !compensationType
      ) {
        throw new Error(
          t(
            "compensation.form.errors.compensation_type_required",
            {
              defaultValue:
                "Selecione o tipo de compensação.",
            }
          )
        );
      }

      const parsedMultiplier =
        payMultiplier ===
        ""
          ? null
          : Number(
              payMultiplier
            );

      const parsedCompensationMinutes =
        compensationMinutes ===
        ""
          ? null
          : Number(
              compensationMinutes
            );

      const parsedAmount =
        amountOverride ===
        ""
          ? null
          : Number(
              amountOverride
            );

      const cleanCurrency =
        String(
          currency ||
            ""
        )
          .trim()
          .toUpperCase();

      if (
        usesExtraPay &&
        parsedMultiplier ===
          null &&
        parsedAmount ===
          null
      ) {
        throw new Error(
          t(
            "compensation.form.errors.payment_method_required",
            {
              defaultValue:
                "Indique um multiplicador de pagamento ou um valor manual.",
            }
          )
        );
      }

      if (
        parsedAmount !==
          null &&
        !cleanCurrency
      ) {
        throw new Error(
          t(
            "compensation.form.errors.currency_required",
            {
              defaultValue:
                "Indique a moeda do valor manual.",
            }
          )
        );
      }

      if (
        parsedMultiplier !==
          null &&
        (
          !Number.isFinite(
            parsedMultiplier
          ) ||
          parsedMultiplier <
            0
        )
      ) {
        throw new Error(
          t(
            "compensation.form.errors.multiplier_invalid",
            {
              defaultValue:
                "Indique um multiplicador válido.",
            }
          )
        );
      }

      if (
        parsedCompensationMinutes !==
          null &&
        (
          !Number.isFinite(
            parsedCompensationMinutes
          ) ||
          parsedCompensationMinutes <
            0
        )
      ) {
        throw new Error(
          t(
            "compensation.form.errors.compensation_minutes_invalid",
            {
              defaultValue:
                "Indique minutos de compensação válidos.",
            }
          )
        );
      }

      if (
        parsedAmount !==
          null &&
        (
          !Number.isFinite(
            parsedAmount
          ) ||
          parsedAmount <
            0
        )
      ) {
        throw new Error(
          t(
            "compensation.form.errors.amount_invalid",
            {
              defaultValue:
                "Indique um valor válido.",
            }
          )
        );
      }

      return {
        publicHolidayId:
          Number(
            resolvedHolidayId
          ),

        startAt:
          start.toISOString(),

        endAt:
          end.toISOString(),

        breakMinutes:
          parsedBreak,

        /*
         * IMPORTANT:
         * workedMinutes is deliberately NOT sent.
         * Backend calculates it.
         */
        compensationType,

        payMultiplier:
          usesExtraPay &&
          parsedAmount ===
            null
            ? parsedMultiplier
            : null,

        compensationMinutes:
          usesTimeOff
            ? parsedCompensationMinutes
            : null,

        amountOverride:
          usesExtraPay
            ? parsedAmount
            : null,

        currency:
          parsedAmount !==
            null
            ? cleanCurrency
            : String(
                currency ||
                  "EUR"
              )
                .trim()
                .toUpperCase(),

        notes:
          notes.trim() ||
          null,
      };
    };

  /* =======================================================
   * SAVE
   * ===================================================== */

  const handleSave =
    async () => {
      setError(
        null
      );

      setConfirmDelete(
        false
      );

      try {
        let resolvedHolidayId =
          publicHolidayId;

        let resolvedHolidayDate =
          selectedHoliday?.date ||
          "";

        setSaving(
          true
        );

        /*
         * Quick creation of a new public holiday.
         */
        if (
          isNewHoliday
        ) {
          if (
            !newHolidayDate
          ) {
            throw new Error(
              t(
                "compensation.form.errors.new_holiday_date_required",
                {
                  defaultValue:
                    "Indique a data do novo feriado.",
                }
              )
            );
          }

          const cleanCountry =
            String(
              newHolidayCountryCode ||
                ""
            )
              .trim()
              .toUpperCase();

          if (
            !cleanCountry
          ) {
            throw new Error(
              t(
                "compensation.form.errors.new_holiday_country_required",
                {
                  defaultValue:
                    "Indique o país do novo feriado.",
                }
              )
            );
          }

          const createdHoliday =
            await createPersonnelPublicHoliday({
              name:
                newHolidayName.trim(),

              date:
                newHolidayDate,

              countryCode:
                cleanCountry,

              regionCode:
                null,

              isRecurring:
                newHolidayRecurring,

              isPaidHoliday:
                newHolidayPaid,

              notes:
                null,
            });

          if (
            !createdHoliday?.id
          ) {
            throw new Error(
              t(
                "compensation.form.errors.new_holiday_failed",
                {
                  defaultValue:
                    "O novo feriado não pôde ser criado.",
                }
              )
            );
          }

          resolvedHolidayId =
            createdHoliday.id;

          resolvedHolidayDate =
            createdHoliday.date;

          setAvailableHolidays(
            (
              current
            ) => [
              ...current.filter(
                (
                  item
                ) =>
                  Number(
                    item.id
                  ) !==
                  Number(
                    createdHoliday.id
                  )
              ),

              createdHoliday,
            ]
          );

          setPublicHolidayId(
            String(
              createdHoliday.id
            )
          );

          markPersonnelResourceStale(
            "publicHolidays"
          );
        }

        const sharedPayload =
          buildPayload(
            resolvedHolidayId,
            resolvedHolidayDate
          );

        if (isEdit) {
          const person =
            selectedPersonOptions[
              0
            ];

          await updatePersonnelHolidayWork(
            work.id,
            {
              ...sharedPayload,

              personType:
                person.personType,

              personId:
                Number(
                  person.personId
                ),
            }
          );
        } else if (
          selectedPersonOptions.length >
          1
        ) {
          await createPersonnelHolidayWorkBulk({
            ...sharedPayload,

            people:
              selectedPersonOptions.map(
                (
                  person
                ) => ({
                  personType:
                    person.personType,

                  personId:
                    Number(
                      person.personId
                    ),
                })
              ),
          });
        } else {
          const person =
            selectedPersonOptions[
              0
            ];

          await createPersonnelHolidayWork({
            ...sharedPayload,

            personType:
              person.personType,

            personId:
              Number(
                person.personId
              ),
          });
        }

        markPersonnelResourceStale(
          "holidayWork"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              isEdit
                ? "updated"
                : "created",
          });
        }

        onClose();
      } catch (
        saveError
      ) {
        console.error(
          "[PersonnelHolidayWorkForm] save failed:",
          saveError
        );

        setError(
          saveError
            ?.response
            ?.data
            ?.error ||
          saveError
            ?.message ||
          t(
            "compensation.form.errors.save_failed",
            {
              defaultValue:
                "Não foi possível guardar o registo.",
            }
          )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  /* =======================================================
   * DELETE
   * ===================================================== */

  const handleDelete =
    async () => {
      if (
        !isEdit ||
        work.status !==
          "DRAFT"
      ) {
        return;
      }

      setError(
        null
      );

      try {
        setDeleting(
          true
        );

        await deletePersonnelHolidayWork(
          work.id
        );

        markPersonnelResourceStale(
          "holidayWork"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              "deleted",
          });
        }

        onClose();
      } catch (
        deleteError
      ) {
        console.error(
          "[PersonnelHolidayWorkForm] delete failed:",
          deleteError
        );

        setError(
          deleteError
            ?.response
            ?.data
            ?.error ||
          deleteError
            ?.message ||
          t(
            "compensation.form.errors.delete_failed",
            {
              defaultValue:
                "Não foi possível eliminar o registo.",
            }
          )
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  /* =======================================================
   * RENDER
   * ===================================================== */

  return (
    <div
      className="personnel-holiday-work-form-overlay"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
            event.currentTarget &&
          !busy
        ) {
          onClose();
        }
      }}
    >
      <div
        className="personnel-holiday-work-form-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="personnel-holiday-work-form-header">
          <div>
            <span>
              {isEdit
                ? t(
                    "compensation.form.edit_kicker",
                    {
                      defaultValue:
                        "Editar registo",
                    }
                  )
                : t(
                    "compensation.form.create_kicker",
                    {
                      defaultValue:
                        "Novo registo",
                    }
                  )}
            </span>

            <h3>
              {isEdit
                ? t(
                    "compensation.form.edit_title",
                    {
                      defaultValue:
                        "Editar trabalho em feriado",
                    }
                  )
                : t(
                    "compensation.form.create_title",
                    {
                      defaultValue:
                        "Registar trabalho em feriado",
                    }
                  )}
            </h3>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              busy
            }
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="personnel-holiday-work-form-grid">
          <div className="personnel-holiday-work-field personnel-holiday-work-field--wide">
            <div className="personnel-holiday-work-people-heading">
              <span>
                {t(
                  "compensation.form.people",
                  {
                    defaultValue:
                      "Pessoas que trabalharam",
                  }
                )}
              </span>

              {!isEdit && (
                <div className="personnel-holiday-work-people-heading-actions">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedPersonKeys(
                        flatPersonOptions.map(
                          (
                            option
                          ) =>
                            option.value
                        )
                      )
                    }
                    disabled={
                      busy ||
                      flatPersonOptions.length ===
                        0 ||
                      selectedPersonKeys.length ===
                        flatPersonOptions.length
                    }
                  >
                    {t(
                      "compensation.form.select_all_people",
                      {
                        defaultValue:
                          "Selecionar todas",
                      }
                    )}
                  </button>

                  {selectedPersonKeys.length >
                    0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedPersonKeys(
                          []
                        )
                      }
                      disabled={
                        busy
                      }
                    >
                      {t(
                        "compensation.form.clear_people",
                        {
                          defaultValue:
                            "Limpar",
                        }
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>

            <Select
              className="personnel-holiday-work-people-select"
              classNamePrefix="personnel-holiday-work-rs"
              value={
                isEdit
                  ? selectedPersonOptions[
                      0
                    ] ||
                    null
                  : selectedPersonOptions
              }
              options={
                personOptions
              }
              onChange={(
                value
              ) => {
                if (
                  isEdit
                ) {
                  setSelectedPersonKeys(
                    value
                      ? [
                          value.value,
                        ]
                      : []
                  );

                  return;
                }

                setSelectedPersonKeys(
                  Array.isArray(
                    value
                  )
                    ? value.map(
                        (
                          option
                        ) =>
                          option.value
                      )
                    : []
                );
              }}
              isMulti={
                !isEdit
              }
              isSearchable
              isClearable
              closeMenuOnSelect={
                isEdit
              }
              hideSelectedOptions={
                false
              }
              isDisabled={
                busy ||
                isEdit
              }
              placeholder={t(
                "compensation.form.select_people",
                {
                  defaultValue:
                    "Pesquisar utilizadores ou motoristas...",
                }
              )}
              noOptionsMessage={() =>
                t(
                  "compensation.form.no_people",
                  {
                    defaultValue:
                      "Nenhuma pessoa encontrada",
                  }
                )
              }
              formatGroupLabel={(
                group
              ) => (
                <div className="personnel-holiday-work-people-group">
                  <span>
                    {
                      group.label
                    }
                  </span>

                  <strong>
                    {
                      group.options
                        .length
                    }
                  </strong>
                </div>
              )}
              formatOptionLabel={(
                option,
                {
                  context,
                }
              ) => {
                if (
                  context ===
                  "value"
                ) {
                  return (
                    <span className="personnel-holiday-work-person-value">
                      {option.personType ===
                      "DRIVER"
                        ? (
                          <LuTruck />
                        )
                        : (
                          <LuUser />
                        )}

                      {
                        option.label
                      }
                    </span>
                  );
                }

                return (
                  <div className="personnel-holiday-work-person-option">
                    <span className="personnel-holiday-work-person-option-icon">
                      {option.personType ===
                      "DRIVER"
                        ? (
                          <LuTruck />
                        )
                        : (
                          <LuUser />
                        )}
                    </span>

                    <div>
                      <strong>
                        {
                          option.label
                        }
                      </strong>

                      <span>
                        {option.personType ===
                        "DRIVER"
                          ? t(
                              "requests.filters.drivers",
                              {
                                defaultValue:
                                  "Motorista",
                              }
                            )
                          : t(
                              "requests.filters.users",
                              {
                                defaultValue:
                                  "Utilizador",
                              }
                            )}
                      </span>
                    </div>
                  </div>
                );
              }}
              menuPortalTarget={
                document.body
              }
              menuPosition="fixed"
              styles={{
                menuPortal:
                  (
                    base
                  ) => ({
                    ...base,
                    zIndex:
                      9600,
                  }),
              }}
            />

            {!isEdit && (
              <div className="personnel-holiday-work-people-tools">
                <span>
                  {t(
                    "compensation.form.people_selected",
                    {
                      defaultValue:
                        "{{count}} selecionadas",

                      count:
                        selectedPersonOptions.length,
                    }
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="personnel-holiday-work-field">
            <span>
              {t(
                "compensation.form.holiday",
                {
                  defaultValue:
                    "Feriado",
                }
              )}
            </span>

            <CreatableSelect
              className="personnel-holiday-work-react-select"
              classNamePrefix="personnel-holiday-work-rs"
              value={
                selectedHolidayOption
              }
              options={
                holidayOptions
              }
              onChange={(
                option
              ) => {
                if (!option) {
                  setPublicHolidayId(
                    ""
                  );

                  setNewHolidayName(
                    ""
                  );

                  setNewHolidayDate(
                    ""
                  );

                  return;
                }

                if (
                  option.value ===
                  "__new__"
                ) {
                  return;
                }

                setPublicHolidayId(
                  option.value
                );

                setNewHolidayName(
                  ""
                );

                setNewHolidayDate(
                  ""
                );
              }}
              onCreateOption={
                canCreateHoliday
                  ? handleCreateHolidayOption
                  : undefined
              }
              isSearchable
              isClearable
              isDisabled={
                busy
              }
              placeholder={t(
                "compensation.form.select_holiday",
                {
                  defaultValue:
                    "Pesquisar ou escrever feriado...",
                }
              )}
              formatCreateLabel={(
                inputValue
              ) =>
                t(
                  "compensation.form.create_named_holiday",
                  {
                    defaultValue:
                      `Criar "${inputValue}"`,

                    name:
                      inputValue,
                  }
                )
              }
              noOptionsMessage={() =>
                t(
                  "compensation.form.no_holidays",
                  {
                    defaultValue:
                      "Nenhum feriado encontrado",
                  }
                )
              }
              formatOptionLabel={(
                option,
                {
                  context,
                }
              ) => {
                const holiday =
                  option?.holiday;

                const optionName =
                  holiday?.name ||
                  option?.label ||
                  "";

                const optionDate =
                  holiday?.date ||
                  "";

                if (
                  context ===
                  "value"
                ) {
                  return optionName;
                }

                return (
                  <div className="personnel-holiday-work-option">
                    <strong>
                      {
                        optionName
                      }
                    </strong>

                    {optionDate && (
                      <span>
                        {formatHolidayDate(
                          optionDate
                        )}
                      </span>
                    )}
                  </div>
                );
              }}
              menuPortalTarget={
                document.body
              }
              menuPosition="fixed"
              styles={{
                menuPortal:
                  (
                    base
                  ) => ({
                    ...base,
                    zIndex:
                      9600,
                  }),
              }}
            />
          </div>

          <label className="personnel-holiday-work-field">
            <span>
              {t(
                "compensation.form.holiday_date",
                {
                  defaultValue:
                    "Data do feriado",
                }
              )}
            </span>

            <div className="personnel-holiday-work-input-icon">
              <LuCalendarDays />

              <input
                type="date"
                value={
                  activeHolidayDate
                }
                onChange={(
                  event
                ) =>
                  setNewHolidayDate(
                    event.target
                      .value
                  )
                }
                readOnly={
                  !isNewHoliday
                }
                disabled={
                  busy ||
                  (
                    !isNewHoliday &&
                    !selectedHoliday
                  )
                }
                className={
                  !isNewHoliday
                    ? "is-readonly"
                    : ""
                }
              />
            </div>

            <small className="personnel-holiday-work-date-help">
              {isNewHoliday
                ? t(
                    "compensation.form.new_holiday_date_help",
                    {
                      defaultValue:
                        "Novo feriado — escolha a respetiva data.",
                    }
                  )
                : t(
                    "compensation.form.holiday_date_help",
                    {
                      defaultValue:
                        "A data é definida pelo feriado selecionado.",
                    }
                  )}
            </small>
          </label>

          {isNewHoliday && (
            <div className="personnel-holiday-work-quick-holiday personnel-holiday-work-field--wide">
              <div className="personnel-holiday-work-quick-holiday-heading">
                <div>
                  <strong>
                    {t(
                      "compensation.form.quick_holiday_title",
                      {
                        defaultValue:
                          "Novo feriado",
                      }
                    )}
                  </strong>

                  <span>
                    {t(
                      "compensation.form.quick_holiday_help",
                      {
                        defaultValue:
                          "Será também adicionado à lista de feriados da empresa.",
                      }
                    )}
                  </span>
                </div>
              </div>

              <div className="personnel-holiday-work-quick-holiday-controls">
                <label className="personnel-holiday-work-quick-field">
                  <span>
                    {t(
                      "compensation.form.holiday_country",
                      {
                        defaultValue:
                          "País",
                      }
                    )}
                  </span>

                  <input
                    type="text"
                    maxLength={2}
                    value={
                      newHolidayCountryCode
                    }
                    onChange={(
                      event
                    ) =>
                      setNewHolidayCountryCode(
                        event.target
                          .value
                          .toUpperCase()
                      )
                    }
                    placeholder={t(
                      "compensation.form.country_placeholder",
                      {
                        defaultValue:
                          "Ex.: PT",
                      }
                    )}
                    disabled={
                      busy
                    }
                  />
                </label>

                <div className="personnel-holiday-work-quick-field">
                  <span>
                    {t(
                      "compensation.form.holiday_recurring",
                      {
                        defaultValue:
                          "Recorrente",
                      }
                    )}
                  </span>

                  <label className="personnel-holiday-work-quick-check">
                    <input
                      type="checkbox"
                      checked={
                        newHolidayRecurring
                      }
                      onChange={(
                        event
                      ) =>
                        setNewHolidayRecurring(
                          event.target
                            .checked
                        )
                      }
                      disabled={
                        busy
                      }
                    />

                    <strong>
                      {t(
                        "compensation.form.recurring_option",
                        {
                          defaultValue:
                            "Repete todos os anos",
                        }
                      )}
                    </strong>
                  </label>
                </div>

                <div className="personnel-holiday-work-quick-field">
                  <span>
                    {t(
                      "compensation.form.holiday_paid",
                      {
                        defaultValue:
                          "Pagamento",
                      }
                    )}
                  </span>

                  <label className="personnel-holiday-work-quick-check">
                    <input
                      type="checkbox"
                      checked={
                        newHolidayPaid
                      }
                      onChange={(
                        event
                      ) =>
                        setNewHolidayPaid(
                          event.target
                            .checked
                        )
                      }
                      disabled={
                        busy
                      }
                    />

                    <strong>
                      {t(
                        "compensation.form.paid_option",
                        {
                          defaultValue:
                            "Feriado pago",
                        }
                      )}
                    </strong>
                  </label>
                </div>
              </div>
            </div>
          )}

          <label className="personnel-holiday-work-field">
            <span>
              {t(
                "compensation.form.start_time",
                {
                  defaultValue:
                    "Hora inicial",
                }
              )}
            </span>

            <div className="personnel-holiday-work-input-icon">
              <LuClock3 />

              <input
                type="time"
                value={
                  startTime
                }
                onChange={(
                  event
                ) =>
                  setStartTime(
                    event.target
                      .value
                  )
                }
                disabled={
                  busy
                }
              />
            </div>
          </label>

          <label className="personnel-holiday-work-field">
            <span>
              {t(
                "compensation.form.end_time",
                {
                  defaultValue:
                    "Hora final",
                }
              )}
            </span>

            <div className="personnel-holiday-work-input-icon">
              <LuClock3 />

              <input
                type="time"
                value={
                  endTime
                }
                onChange={(
                  event
                ) =>
                  setEndTime(
                    event.target
                      .value
                  )
                }
                disabled={
                  busy
                }
              />
            </div>
          </label>

          <label className="personnel-holiday-work-field">
            <span>
              {t(
                "compensation.form.break_minutes",
                {
                  defaultValue:
                    "Pausa (minutos)",
                }
              )}
            </span>

            <input
              type="number"
              min="0"
              step="1"
              value={
                breakMinutes
              }
              onChange={(
                event
              ) =>
                setBreakMinutes(
                  event.target
                    .value
                )
              }
              disabled={
                busy
              }
            />
          </label>

          <div className="personnel-holiday-work-estimate">
            <span>
              {t(
                "compensation.form.worked_estimate",
                {
                  defaultValue:
                    "Estimativa trabalhada",
                }
              )}
            </span>

            <strong>
              {formatMinutes(
                estimatedWorkedMinutes,
                t
              )}
            </strong>

            <small>
              {t(
                "compensation.form.backend_calculated",
                {
                  defaultValue:
                    "O valor final é calculado pelo servidor.",
                }
              )}
            </small>
          </div>

          <label className="personnel-holiday-work-field personnel-holiday-work-field--wide">
            <span>
              {t(
                "compensation.form.compensation_type",
                {
                  defaultValue:
                    "Tipo de compensação",
                }
              )}
            </span>

            <select
              value={
                compensationType
              }
              onChange={(
                event
              ) =>
                setCompensationType(
                  event.target
                    .value
                )
              }
              disabled={
                busy
              }
            >
              <option
                value=""
                disabled
              >
                {t(
                  "compensation.form.select_compensation_type",
                  {
                    defaultValue:
                      "Selecionar compensação...",
                  }
                )}
              </option>

              {COMPENSATION_TYPES.map(
                (
                  type
                ) => (
                  <option
                    key={
                      type
                    }
                    value={
                      type
                    }
                  >
                    {compensationTypeLabel(
                      type
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          {usesExtraPay && (
            <>
              <label className="personnel-holiday-work-field">
                <span>
                  {t(
                    "compensation.form.pay_multiplier",
                    {
                      defaultValue:
                        "Fator sobre a remuneração",
                    }
                  )}
                </span>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    payMultiplier
                  }
                  onChange={(
                    event
                  ) => {
                    const value =
                      event.target
                        .value;

                    setPayMultiplier(
                      value
                    );

                    if (
                      value !==
                      ""
                    ) {
                      setAmountOverride(
                        ""
                      );
                    }
                  }}
                  placeholder={t(
                    "compensation.form.pay_multiplier_placeholder",
                    {
                      defaultValue:
                        "Ex.: 1,50",
                    }
                  )}
                  disabled={
                    busy
                  }
                />

                <small className="personnel-holiday-work-field-help">
                  {t(
                    "compensation.form.pay_multiplier_help",
                    {
                      defaultValue:
                        "Indique apenas o fator: 1,00× = remuneração normal, 1,50× = 150%, 2,00× = 200%. As horas trabalhadas são calculadas automaticamente pelo horário acima.",
                    }
                  )}
                </small>
              </label>

              <label className="personnel-holiday-work-field">
                <span>
                  {t(
                    "compensation.form.amount_override",
                    {
                      defaultValue:
                        "Valor manual",
                    }
                  )}
                </span>

                <div className="personnel-holiday-work-money">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      amountOverride
                    }
                    onChange={(
                      event
                    ) => {
                      const value =
                        event.target
                          .value;

                      setAmountOverride(
                        value
                      );

                      if (
                        value !==
                        ""
                      ) {
                        setPayMultiplier(
                          ""
                        );
                      }
                    }}
                    placeholder={t(
                      "compensation.form.amount_override_placeholder",
                      {
                        defaultValue:
                          "Ex.: 75,00",
                      }
                    )}
                    disabled={
                      busy
                    }
                  />

                  <input
                    type="text"
                    maxLength={3}
                    value={
                      currency
                    }
                    onChange={(
                      event
                    ) =>
                      setCurrency(
                        event.target
                          .value
                          .toUpperCase()
                      )
                    }
                    placeholder="EUR"
                    aria-label={t(
                      "compensation.form.currency",
                      {
                        defaultValue:
                          "Moeda",
                      }
                    )}
                    title={t(
                      "compensation.form.currency",
                      {
                        defaultValue:
                          "Moeda",
                      }
                    )}
                    disabled={
                      busy
                    }
                  />
                </div>

                <small className="personnel-holiday-work-field-help">
                  {t(
                    "compensation.form.amount_override_help",
                    {
                      defaultValue:
                        "Alternativa ao fator. Preencha apenas quando quiser definir diretamente o montante final, por exemplo 75,00 EUR. Deixe vazio quando utilizar um fator.",
                    }
                  )}
                </small>
              </label>

              <div className="personnel-holiday-work-pay-help personnel-holiday-work-field--wide">
                <strong>
                  {t(
                    "compensation.form.payment_method_help_title",
                    {
                      defaultValue:
                        "Como funciona?",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "compensation.form.payment_method_help",
                    {
                      defaultValue:
                        "Escolha apenas um método. Use o fator quando o pagamento depender da remuneração normal da pessoa; o Personnel calcula o equivalente remuneratório. Use Valor manual apenas quando quiser definir diretamente o montante final em dinheiro.",
                    }
                  )}
                </span>
              </div>

              <div className="personnel-holiday-work-payment-result personnel-holiday-work-field--wide">
                <div className="personnel-holiday-work-payment-result-heading">
                  <span>
                    {t(
                      "compensation.form.payment_result",
                      {
                        defaultValue:
                          "Resultado",
                      }
                    )}
                  </span>

                  {amountOverride !==
                  "" ? (
                    <strong>
                      {t(
                        "compensation.form.manual_payment",
                        {
                          defaultValue:
                            "Valor manual",
                        }
                      )}
                    </strong>
                  ) : parsedPayMultiplier ? (
                    <strong>
                      {t(
                        "compensation.form.multiplier_payment",
                        {
                          defaultValue:
                            "Multiplicador",
                        }
                      )}
                    </strong>
                  ) : null}
                </div>

                {amountOverride !==
                "" ? (
                  <div className="personnel-holiday-work-payment-result-content">
                    <strong>
                      {amountOverride}
                      {" "}
                      {currency ||
                        "—"}
                    </strong>

                    <span>
                      {t(
                        "compensation.form.manual_payment_result_help",
                        {
                          defaultValue:
                            "Este é o montante final definido manualmente para este registo. Não precisa de preencher o fator e não será aplicado qualquer cálculo por remuneração.",
                        }
                      )}
                    </span>
                  </div>
                ) : parsedPayMultiplier ? (
                  <div className="personnel-holiday-work-payment-result-content">
                    <strong>
                      {formatMinutes(
                        estimatedWorkedMinutes,
                        t
                      )}
                      {" × "}
                      {parsedPayMultiplier}
                      {" = "}
                      {formatMinutes(
                        estimatedPayEquivalentMinutes,
                        t
                      )}
                      {" "}
                      {t(
                        "compensation.form.equivalent",
                        {
                          defaultValue:
                            "equivalentes",
                        }
                      )}
                    </strong>

                    <span>
                      {t(
                        "compensation.form.multiplier_result_help",
                        {
                          defaultValue:
                            "Cálculo concluído no Personnel. Não precisa de preencher o Valor manual. Este resultado representa o equivalente remuneratório; o módulo Salaries usará depois a remuneração da pessoa para determinar o montante final em dinheiro.",
                        }
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="personnel-holiday-work-payment-result-empty">
                    {t(
                      "compensation.form.payment_result_empty",
                      {
                        defaultValue:
                          "Preencha o multiplicador ou introduza um valor manual para ver o resultado.",
                      }
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {usesTimeOff && (
            <label className="personnel-holiday-work-field personnel-holiday-work-field--wide">
              <span>
                {t(
                  "compensation.form.compensation_minutes",
                  {
                    defaultValue:
                      "Tempo de compensação (minutos)",
                  }
                )}
              </span>

              <input
                type="number"
                min="0"
                step="1"
                value={
                  compensationMinutes
                }
                onChange={(
                  event
                ) =>
                  setCompensationMinutes(
                    event.target
                      .value
                  )
                }
                placeholder={String(
                  estimatedWorkedMinutes ||
                    ""
                )}
                disabled={
                  busy
                }
              />
            </label>
          )}

          <label className="personnel-holiday-work-field personnel-holiday-work-field--wide">
            <span>
              {t(
                "compensation.form.notes",
                {
                  defaultValue:
                    "Notas",
                }
              )}
            </span>

            <textarea
              rows={3}
              value={
                notes
              }
              onChange={(
                event
              ) =>
                setNotes(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "compensation.form.notes_placeholder",
                {
                  defaultValue:
                    "Informação adicional, se aplicável...",
                }
              )}
              disabled={
                busy
              }
            />
          </label>
        </div>

        {error && (
          <div className="personnel-holiday-work-form-error">
            {
              error
            }
          </div>
        )}

        {confirmDelete ? (
          <div className="personnel-holiday-work-delete-confirm">
            <p>
              {t(
                "compensation.form.confirm_delete",
                {
                  defaultValue:
                    "Eliminar permanentemente este rascunho? Esta ação não pode ser anulada.",
                }
              )}
            </p>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={() =>
                  setConfirmDelete(
                    false
                  )
                }
                disabled={
                  busy
                }
              >
                {t(
                  "requests.actions.back",
                  {
                    defaultValue:
                      "Voltar",
                  }
                )}
              </button>

              <button
                type="button"
                className="personnel-request-action-btn is-reject"
                onClick={
                  handleDelete
                }
                disabled={
                  busy
                }
              >
                {deleting
                  ? t(
                      "requests.actions.working",
                      {
                        defaultValue:
                          "A processar...",
                      }
                    )
                  : t(
                      "compensation.form.delete_confirm",
                      {
                        defaultValue:
                          "Eliminar definitivamente",
                      }
                    )}
              </button>
            </div>
          </div>
        ) : (
          <div className="personnel-holiday-work-form-actions">
            <div>
              {canDelete &&
                isEdit &&
                work.status ===
                  "DRAFT" && (
                  <button
                    type="button"
                    className="personnel-holiday-work-delete-btn"
                    onClick={() =>
                      setConfirmDelete(
                        true
                      )
                    }
                    disabled={
                      busy
                    }
                  >
                    <LuTrash2 />

                    {t(
                      "compensation.form.delete",
                      {
                        defaultValue:
                          "Eliminar registo",
                      }
                    )}
                  </button>
                )}
            </div>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={
                  onClose
                }
                disabled={
                  busy
                }
              >
                {t(
                  "requests.actions.close",
                  {
                    defaultValue:
                      "Fechar",
                  }
                )}
              </button>

              <button
                type="button"
                className="personnel-holiday-work-save-btn"
                onClick={
                  handleSave
                }
                disabled={
                  busy
                }
              >
                <LuSave />

                {saving
                  ? t(
                      "requests.actions.working",
                      {
                        defaultValue:
                          "A processar...",
                      }
                    )
                  : isEdit
                  ? t(
                      "compensation.form.save",
                      {
                        defaultValue:
                          "Guardar registo",
                      }
                    )
                  : selectedPersonOptions.length >
                    1
                  ? t(
                      "compensation.form.save_multiple",
                      {
                        defaultValue:
                          "Guardar {{count}} registos",

                        count:
                          selectedPersonOptions.length,
                      }
                    )
                  : t(
                      "compensation.form.save",
                      {
                        defaultValue:
                          "Guardar registo",
                      }
                    )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}